/**
 * `localDeployTarget` — stand a workspace up as a local child process and reach
 * it over `ws://127.0.0.1:<port>`. `tlsTermination: "none"` (loopback only).
 *
 * The `command` / `args` / `readyPattern` config fields exist so the target is
 * testable hermetically: a test points them at a mock server instead of a real
 * `skaile serve`. Defaults resolve the `skaile` bin from this package and run
 * `skaile serve --port <port>`.
 */

import { createRequire } from "node:module";
import { dirname, resolve as resolvePath } from "node:path";
import type { DeployTarget } from "@skaile/workspaces/plugin-registry";
import * as z from "zod";
import { allocateFreePort } from "./port.js";
import { buildProcessHandle, pidAlive, spawnProcess } from "./process-handle.js";
import { waitForLine } from "./stream-lines.js";

const localConfigSchema = z
  .object({
    port: z.number().int().positive().optional(),
    env: z.record(z.string(), z.string()).optional(),
    cwd: z.string().optional(),
    command: z.string().optional(),
    args: z.array(z.string()).optional(),
    // `skaile serve` emits structured logs rather than a single banner; the
    // ws:// URL line is the most stable readiness marker.
    readyPattern: z.string().optional(),
  })
  .strict()
  .default({});

type LocalConfig = z.infer<typeof localConfigSchema>;

const localPayloadSchema = z.object({
  pid: z.number(),
  port: z.number(),
  cwd: z.string(),
});

type LocalPayload = z.infer<typeof localPayloadSchema>;

const DEFAULT_READY_PATTERN = "ws://";
const DEFAULT_READY_TIMEOUT_MS = 30_000;

/** Resolve the `skaile` bin shipped by this package, for the default command. */
function resolveSkaileBin(): string {
  const require = createRequire(import.meta.url);
  const pkgPath = require.resolve("@skaile/workspaces/package.json");
  const pkg = require("@skaile/workspaces/package.json") as { bin?: Record<string, string> };
  const rel = pkg.bin?.skaile ?? "dist/cli/index.js";
  return resolvePath(dirname(pkgPath), rel);
}

/** Built-in target that runs the workspace as a local child process. */
export const localDeployTarget: DeployTarget<LocalConfig, LocalPayload> = {
  id: "local",
  displayName: "Local process",
  apiVersion: 1,
  tlsTermination: "none",
  configSchema: localConfigSchema as unknown as z.ZodType<LocalConfig>,
  payloadSchema: localPayloadSchema,

  async create(config, ctx) {
    const log = ctx.log;
    const port = config.port ?? (await allocateFreePort());
    const cwd = config.cwd ?? process.cwd();
    const command = config.command ?? resolveSkaileBin();
    const args = config.args ?? ["serve", "--port", String(port)];
    const pattern = new RegExp(config.readyPattern ?? DEFAULT_READY_PATTERN);

    // `ctx.env` is the host-injected per-session env; `config.env` overrides it.
    const spawned = spawnProcess(command, args, {
      cwd,
      env: { ...process.env, ...ctx.env, ...config.env },
    });
    // portableSpawn returns pid -1 on spawn failure; a -1 pid would later turn
    // process.kill(pid) into a kill(-1) broadcast to every process we own.
    if (!spawned.proc.pid || spawned.proc.pid <= 0) {
      throw new Error("failed to spawn local process");
    }
    log.info("local process spawned", { pid: spawned.proc.pid, port, command });

    const payload: LocalPayload = { pid: spawned.proc.pid, port, cwd };
    const ready = (timeoutMs: number, signal: AbortSignal) =>
      waitForLine(spawned.buffer, pattern, { timeoutMs, signal });
    return buildProcessHandle(payload, ctx, ready, spawned, DEFAULT_READY_TIMEOUT_MS);
  },

  async restore(payload, ctx) {
    const log = ctx.log;
    if (!pidAlive(payload.pid)) {
      throw new Error(`local deploy pid ${payload.pid} is no longer alive`);
    }
    log.info("re-attached to local process", { pid: payload.pid, port: payload.port });
    // Re-attached handle is already running; readiness is a no-op.
    return buildProcessHandle(payload, ctx, async () => {}, null, DEFAULT_READY_TIMEOUT_MS);
  },
};
